import { Center, FormControl, FormErrorMessage, FormLabel, Stack, Text } from '@chakra-ui/react'
import React, { FC, useCallback } from 'react'
import { SubmitErrorHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useTitle } from 'react-use'
import { useLocation } from 'wouter'
import { apiConfig } from '../../../common/apiConfig'
import { TextLink } from '../../../common/components/TextLink'
import { FormControlWrapper } from '../../../common/form/FormInput'
import { PasswordInput } from '../../../component/manager/common/atoms/passwordInput/PasswordInput'
import { ApplicantProfileApi } from '../../../generated'
import { signOut } from '../../hooks/useApplicant'
import { Button } from '../Button'
import { Title } from '../Title'

interface FormType {
  currentPassword: string
  newPassword: string
  password_repeat: string
}

export const PasswordChangeForm: FC = () => {
  const [t] = useTranslation()
  useTitle(t('SEPAS パスワード変更'))

  const { handleSubmit, control, setError, watch } = useForm<FormType>({
    mode: 'onBlur',
  })
  const [, setLocation] = useLocation()

  const registerPassword = useCallback(
    (form) => {
      new ApplicantProfileApi(apiConfig)
        .updatePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword })
        .then(() => {
          setLocation('/password-change-complete')
          return signOut()
        })
        .catch((error) => {
          switch (error.status) {
            case 404:
              setLocation('/')
              break
            case 409:
              setError('currentPassword', {
                type: 'manual',
                message: `パスワードが正しくありません。`,
              })
              break
            default:
              console.log('error', error)
          }
        })
    },
    [setError, setLocation]
  )

  const onError: SubmitErrorHandler<FormType> = useCallback((error, event) => {
    console.info('submit error', error, event)
  }, [])

  const watchedPassword = watch('newPassword')

  return (
    <div>
      <Title text={t('パスワード変更')} />

      <div className={'mt24'}>
        <Stack>
          <FormControlWrapper<FormType>
            control={control}
            path={'currentPassword'}
            skipNormalize={true}
            options={{
              required: '必須入力です。',
            }}
          >
            {({ id, isInvalid, error, ...inputProps }) => (
              <FormControl
                id={id}
                isRequired={true}
                isInvalid={isInvalid ? true : undefined}
                as={'fieldset'}
                display={'flex'}
                flexDir={'row'}
                alignItems={`start`}
              >
                <FormLabel w={'10rem'} fontSize={`14px`} fontWeight={700}>
                  {t('現在のパスワード')}
                </FormLabel>
                <Stack>
                  <PasswordInput size={'sm'} {...inputProps} w={'24rem'} {...inputProps} />
                  {error?.message && <FormErrorMessage>{t(error.message)}</FormErrorMessage>}
                </Stack>
              </FormControl>
            )}
          </FormControlWrapper>

          <FormControlWrapper<FormType>
            control={control}
            path={'newPassword'}
            skipNormalize={true}
            options={{
              required: '必須入力です。',
              pattern: {
                value: /^(?=.*?[a-z])(?=.*?\d)[a-z\d]{8,100}$/i,
                message: '半角英数字をそれぞれ1種類以上含み、8文字以上で入力してください。',
              },
            }}
          >
            {({ id, isInvalid, error, ...inputProps }) => (
              <FormControl
                id={id}
                isRequired={true}
                isInvalid={isInvalid ? true : undefined}
                as={'fieldset'}
                display={'flex'}
                flexDir={'row'}
                alignItems={`start`}
              >
                <FormLabel w={'10rem'} fontSize={`14px`} fontWeight={700}>
                  {t('新しいパスワード')}
                </FormLabel>
                <Stack>
                  <PasswordInput size={'sm'} {...inputProps} w={'24rem'} {...inputProps} />
                  <Text fontSize="xs">
                    {t('半角英数字をそれぞれ1種類以上含み、8文字以上で入力してください。')}
                  </Text>
                  {error?.message && <FormErrorMessage>{t(error.message)}</FormErrorMessage>}
                </Stack>
              </FormControl>
            )}
          </FormControlWrapper>

          <FormControlWrapper<FormType>
            control={control}
            path={'password_repeat'}
            skipNormalize={true}
            options={{
              required: '必須入力です。',
              validate: (value) => value === watchedPassword || 'パスワードが一致していません。',
            }}
          >
            {({ id, isInvalid, error, ...inputProps }) => (
              <FormControl
                id={id}
                isRequired={true}
                isInvalid={isInvalid ? true : undefined}
                as={'fieldset'}
                display={'flex'}
                flexDir={'row'}
                alignItems={`center`}
              >
                <FormLabel w={'10rem'} fontSize={`14px`} fontWeight={700}>
                  {t('パスワード確認')}
                </FormLabel>
                <Stack>
                  <PasswordInput size={'sm'} {...inputProps} w={'24rem'} {...inputProps} />
                  {error?.message && <FormErrorMessage>{t(error.message)}</FormErrorMessage>}
                </Stack>
              </FormControl>
            )}
          </FormControlWrapper>
        </Stack>

        <Center className={'mt16'}>
          <Button
            role={'primary'}
            size={'md'}
            text={t('パスワード変更')}
            w={'200px'}
            onClick={handleSubmit(registerPassword, onError)}
          />
        </Center>

        <Center className={'mt8'}>
          <TextLink text={t('キャンセル')} to={'/applications'} />
        </Center>
      </div>
    </div>
  )
}
